import { inject, Injectable } from '@angular/core';
import { CompoApp } from '../CompoApp';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { ClassTreeNodeEntry } from '../../models/customizable-class-tree/class-tree-node-entry.model';
import { take } from 'rxjs/operators';
import { TranslationCodeView } from '../../models/translation/TranslationCodeView';
import { CustomizableClassTree } from '../../models/customizable-class-tree/customizable-class-tree.model';
import { UrlBuilder } from '../../models/frontend_entities/UrlBuilder';
import Constants from '../../Constants';
import {
  CustomizableClassTreeInternalCodeEnum
} from '../../models/customizable-class-tree/customizable-class-tree-internal-code.enum';
import { ClassTreeNodeEntryInfo } from '../../models/customizable-class-tree/class-tree-node-entry-info-model';

@Injectable({
  providedIn: 'root'
})
export class CustomizableClassTreeRequestService {

  //region INJECTIONS
  private readonly app: CompoApp = inject(CompoApp);
  private readonly http: HttpClient = inject(HttpClient);
  //endregion

  //region GET
  getClassTrees(
    orgId: string
  ): Observable<CustomizableClassTree[]> {
    const url = new UrlBuilder(Constants.URLS.compoClass.sqlAllClassTrees)
      .build();

    return this.http.get<CustomizableClassTree[]>(url)
      .pipe(take(1));
  }

  getClassTree(
    classTreeInternalCode: CustomizableClassTreeInternalCodeEnum,
    orgId: string,
    communityId: string
  ): Observable<CustomizableClassTree> {
    const url = new UrlBuilder(Constants.URLS.compoClass.sqlClassTree)
      .param('classTreeInternalCode', classTreeInternalCode)
      .param('orgId', orgId)
      .param('communityId', communityId)
      .build();

    return this.http.get<CustomizableClassTree>(url)
      .pipe(take(1));
  }

  // Get the classes from a tree.
  getClassLevel(
    organizationId: string,
    treeId: string,
    parentClassId: string | null
  ): Observable<ClassTreeNodeEntry[]> {

    const url = new UrlBuilder(Constants.URLS.compoClass.getClassLevel)
      .param('organizationId', organizationId)
      .param('parentId', parentClassId)
      .param('treeId', treeId)
      .build();

    return this.http.get<ClassTreeNodeEntry[]>(url)
      .pipe(take(1));
  }

  // These are the classes that have objects, therefore, cannot be removed from the tree. ONLY PRODUCT CLASSES!
  getRequiredClassIds(
    orgId: string
  ): Observable<string[]> {
    return from(this.app.classService.getRequiredLeafClassIds(orgId));
  }

  // Search the product classes. (Right side class selector)
  getTreeBranch(searchTerm: string | null, branchRootObjectClassId: string | null, treeId: string | null,
                organizationId: string): Observable<ClassTreeNodeEntry[]> {
    const url = new UrlBuilder(Constants.URLS.compoClass.getTreeBranch)
      .param('branchRootObjectClassId', branchRootObjectClassId)
      .param('treeId', treeId)
      .param('searchTerm', searchTerm)
      .param('organizationId', organizationId)
      .build();

    return this.http.get<ClassTreeNodeEntry[]>(url);
  }

  getAllClassNodesForTree(): Observable<ClassTreeNodeEntryInfo[]> {
    const url = new UrlBuilder(Constants.URLS.compoClass.getAllClassNodesForTree)
      .param('lang', this.app.data.getLang())
      .build();

    return this.http.get<ClassTreeNodeEntryInfo[]>(url);
  }
  //endregion

  //region POST

  // The new ID is returned.
  createClassTree(
    orgId: string,
    name: string,
    setAsDefault: boolean
  ): Observable<string> {
    const url = new UrlBuilder(Constants.URLS.compoClass.createClassTree)
      .build();

    return this.http.post<string>(url, {organizationId: orgId, name: name, isDefaultTree: setAsDefault, community: null, treeInternalCode: 'ORG_TREE'})
      .pipe(take(1));
  }

  // The new ID is returned.
  copyClassTree(
    orgId: string,
    treeId: string,
    name: string,
    setAsDefault: boolean
  ): Observable<string> {
    const url = new UrlBuilder(Constants.URLS.compoClass.copyClassTree)
      .param('treeId', treeId)
      .build();

    return this.http.post<string>(url, {copyId: treeId, orgId: orgId, name: name, isDefaultTree: setAsDefault})
      .pipe(take(1));
  }
  //endregion

  //region PUT
  updateClassTree(
    orgId: string,
    treeId: string,
    tree: Map<string, string[]>, //KEY: parent id, VALUE: children ids. Only those classes are sent which were changed.
                                 //The key for the root level is: 'ROOT'. See: CustomizableClassTreeXsState.ROOT_KEY
    translationMap: Map<string, TranslationCodeView>, //KEY: class id, VALUE: new name
    treeNewEntryMap: Map<string, ClassTreeNodeEntry>,
    lang: string
  ): Observable<void> {
    const url = new UrlBuilder(Constants.URLS.compoClass.updateClassTree)
      .param('treeId', treeId)
      .param('orgId', orgId)
      .param('lang', lang)
      .build();

    const treeObject: Record<string, string[]> = {};
    tree.forEach((value, key) => {
      treeObject[key] = value;
    });
    const translationObject: Record<string, TranslationCodeView> = {};
    translationMap.forEach((value, key) => {
      translationObject[key] = value;
    });

    const treeNewEntryObject: Record<string, ClassTreeNodeEntry> = {};
    treeNewEntryMap.forEach((value, key) => {
      treeNewEntryObject[key] = value;
    });

    const body = JSON.stringify({tree: treeObject, translationMap: translationObject, treeNewEntryMap: treeNewEntryObject});
    return this.http.put<void>(url, body)
      .pipe(take(1));
  }

  deleteClassTree(treeId: string) {
    const url = new UrlBuilder(Constants.URLS.compoClass.deleteClassTree)
      .param('treeId', treeId)
      .build();

    return this.http.delete<void>(url)
  }
  //endregion


}
